/**
 * PSV-1338 — bottom-right ship-upgrade HUD panel.
 *
 * Reads gems + class reactively (caller drives via `setGems` / `setClassId`,
 * sourced from the `Player.listen()` handle in ArenaController). Renders:
 *   - "Tier: N / 3" current → max CLASS tier (PSV-1726 — tier is the ship
 *     CLASS tier from `shared/shipClasses`, 1-based; the legacy PSV-1338
 *     upgradeTier no longer drives this panel)
 *   - "Next: N gems" cost of the next class tier (or "Max tier" at
 *     MAX_CLASS_TIER)
 *   - Upgrade button — clickable when affordable + not at max
 *
 * Click → `onUpgradeClick()` callback (PSV-1726: ArenaController opens the
 * ClassPickerModal from it). Local toast helper is also exposed for
 * `ShipClassChanged` / `ShipClassRejected` feedback.
 */

import Phaser from 'phaser';

import { MAX_CLASS_TIER, classById, nextChoicesFor } from '../../../server/shared/shipClasses';
import { MAX_STAT_LEVEL, STAT_ORDER } from '../../../server/shared/upgradeStats';

const PANEL_WIDTH = 220;
const PANEL_HEIGHT = 110;
const PANEL_MARGIN = 16;

// PSV-1338 Bug 1 (Pass 3) — button hit rect in CONTAINER-LOCAL coords.
// Container-level setInteractive intercepts every click in the panel
// area; the pointerdown handler then checks whether the local-space
// pointer landed inside this sub-rect to decide whether to fire the
// upgrade. Keeping in sync with the visual paint in refresh().
const BUTTON_LOCAL_X = 10; // inset from container left edge
const BUTTON_LOCAL_Y = 64; // inset from container top edge
const BUTTON_WIDTH = PANEL_WIDTH - 20;
const BUTTON_HEIGHT = 28;
const PANEL_BG_COLOR = 0x101521;
const PANEL_BG_ALPHA = 0.88;
const PANEL_BORDER_COLOR = 0x3a4768;

const COLOR_ENABLED = 0x44ff7a;
const COLOR_DISABLED = 0x3a3a3a;
const COLOR_TEXT = '#eeeeee';
const COLOR_TEXT_DIM = '#9aa0b4';
const COLOR_TOAST_OK = '#44ff7a';
const COLOR_TOAST_ERR = '#ff5577';

const TOAST_DURATION_MS = 3_000;
const TOAST_FADE_MS = 400;

export type UpgradeClickHandler = () => void;

interface ToastEntry {
  text: Phaser.GameObjects.Text;
  age: number;
}

export class UpgradePanel {
  private readonly scene: Phaser.Scene;
  private readonly onClick: UpgradeClickHandler;

  private container: Phaser.GameObjects.Container | null = null;
  private background: Phaser.GameObjects.Graphics | null = null;
  private tierText: Phaser.GameObjects.Text | null = null;
  private nextText: Phaser.GameObjects.Text | null = null;
  private buttonBg: Phaser.GameObjects.Graphics | null = null;
  private buttonLabel: Phaser.GameObjects.Text | null = null;
  /**
   * Whether the button is currently in the affordable / clickable state.
   * Updated in `refresh()` from gems/tier; checked in the container's
   * pointerdown handler before firing the upgrade. We DON'T toggle the
   * container's interactive flag — keeping it on so the cursor remains
   * a hand on hover, which matches the older button-only behavior.
   */
  private buttonAffordable = false;

  private currentGems = 0;
  // PSV-1726 — class tiers are 1-based; the starter class is tier 1.
  private currentTier = 1;
  /** PSV-1912 bug — "next cost" must follow the DAG, not the tier row:
   * Crusader's branches cost 350, but the tier-4 row starts with the
   * 500-gem Harvester (Pioneer branch) — over Crusader's 360 hold, so
   * the panel showed an unreachable price and never enabled the button. */
  private currentClassId = 'fly';
  /**
   * PSV-1727 — tier-up is gated on ALL stats maxed (server enforces it;
   * we mirror the gate so the button never looks clickable and then
   * bounces). `statProgress` is total levels bought, for the hint text.
   */
  private statsMaxed = false;
  private statProgress = 0;
  private destroyed = false;

  private readonly toasts: ToastEntry[] = [];

  constructor(scene: Phaser.Scene, onClick: UpgradeClickHandler) {
    this.scene = scene;
    this.onClick = onClick;
    this.build();
    this.refresh();
  }

  setGems(n: number): void {
    if (this.currentGems === n) return;
    this.currentGems = n;
    this.refresh();
  }

  /** PSV-1727 — mirror of the server's all-stats-maxed tier-up gate. */
  setStatsProgress(totalLevels: number): void {
    const maxTotal = STAT_ORDER.length * MAX_STAT_LEVEL;
    const clamped = Math.max(0, Math.min(maxTotal, Math.floor(totalLevels)));
    const maxed = clamped >= maxTotal;
    if (this.statProgress === clamped && this.statsMaxed === maxed) return;
    this.statProgress = clamped;
    this.statsMaxed = maxed;
    this.refresh();
  }

  setClassId(id: string): void {
    const def = classById(id); // tolerates legacy ids (falls back to starter)
    if (this.currentClassId === def.id) return;
    this.currentClassId = def.id;
    this.currentTier = def.tier;
    this.refresh();
  }

  /** Cheapest reachable next class — what the button actually gates on. */
  private cheapestNext(): { cost: number } | null {
    const choices = nextChoicesFor(this.currentClassId);
    if (choices.length === 0) return null;
    return { cost: Math.min(...choices.map((c) => c.cost)) };
  }

  /**
   * Show a transient toast top-center. Color picks: green for OK,
   * red for error. Fades out over the last 400ms of its 3s lifetime.
   */
  showToast(message: string, kind: 'ok' | 'error'): void {
    if (this.destroyed) return;
    const cam = this.scene.cameras.main;
    const color = kind === 'ok' ? COLOR_TOAST_OK : COLOR_TOAST_ERR;
    const text = this.scene.add
      .text(cam.centerX, 80 + this.toasts.length * 28, message, {
        fontSize: '18px',
        color,
        fontStyle: 'bold',
        backgroundColor: '#000000aa',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(25);
    this.toasts.push({ text, age: 0 });
  }

  /** Advance toast lifetimes; caller invokes from ArenaController.update. */
  update(deltaMs: number): void {
    if (this.destroyed || this.toasts.length === 0) return;
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const t = this.toasts[i]!;
      t.age += deltaMs;
      if (t.age >= TOAST_DURATION_MS) {
        t.text.destroy();
        this.toasts.splice(i, 1);
        continue;
      }
      const fadeStart = TOAST_DURATION_MS - TOAST_FADE_MS;
      if (t.age > fadeStart) {
        const fadeProgress = (t.age - fadeStart) / TOAST_FADE_MS;
        t.text.setAlpha(1 - fadeProgress);
      }
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const t of this.toasts) t.text.destroy();
    this.toasts.length = 0;
    this.container?.destroy(true);
    this.container = null;
    this.background = null;
    this.tierText = null;
    this.nextText = null;
    this.buttonBg = null;
    this.buttonLabel = null;
  }

  /**
   * Returns true if the given pointer screen position falls inside the
   * panel's bounding rect. Used by ArenaController.handleFireInput to
   * suppress the fire shot when the user clicks the Upgrade button
   * (otherwise the same pointerdown that the button consumed ALSO
   * reaches the scene-level fire handler).
   *
   * Panel uses `setScrollFactor(0)` → container x/y are in screen
   * coordinates, so we compare against `pointer.x` / `pointer.y` directly
   * (NOT worldX/worldY).
   */
  containsPointer(pointer: { x: number; y: number }): boolean {
    if (this.destroyed || this.container === null) return false;
    const px = pointer.x;
    const py = pointer.y;
    const x0 = this.container.x;
    const y0 = this.container.y;
    return (
      px >= x0 &&
      px <= x0 + PANEL_WIDTH &&
      py >= y0 &&
      py <= y0 + PANEL_HEIGHT
    );
  }

  // ---------- private ----------

  private build(): void {
    const cam = this.scene.cameras.main;
    const panelX = cam.width - PANEL_WIDTH - PANEL_MARGIN;
    const panelY = cam.height - PANEL_HEIGHT - PANEL_MARGIN;

    this.container = this.scene.add
      .container(panelX, panelY)
      .setScrollFactor(0)
      .setDepth(18);

    // Background (drawn at container-local origin).
    this.background = this.scene.add.graphics();
    this.background.fillStyle(PANEL_BG_COLOR, PANEL_BG_ALPHA);
    this.background.fillRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT);
    this.background.lineStyle(1, PANEL_BORDER_COLOR, 1);
    this.background.strokeRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT);
    this.container.add(this.background);

    this.tierText = this.scene.add.text(10, 8, '', {
      fontSize: '14px',
      color: COLOR_TEXT,
      fontStyle: 'bold',
    });
    this.container.add(this.tierText);

    this.nextText = this.scene.add.text(10, 30, '', {
      fontSize: '13px',
      color: COLOR_TEXT_DIM,
      // PSV-1911 QA — the "Stats 41/64 → then N gems" line is wider than
      // the panel and ran off the canvas edge on phones; wrap it inside.
      wordWrap: { width: PANEL_WIDTH - 20 },
    });
    this.container.add(this.nextText);

    // Button bg + label live at fixed offset; refresh() repaints the bg
    // color band based on affordability.
    this.buttonBg = this.scene.add.graphics();
    this.container.add(this.buttonBg);

    this.buttonLabel = this.scene.add
      .text(PANEL_WIDTH / 2, 78, 'Upgrade', {
        fontSize: '16px',
        color: COLOR_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(this.buttonLabel);

    // PSV-1338 Bug 1 (Pass 3) — container-level interactive.
    //
    // Earlier passes put `setInteractive` on a child Rectangle inside the
    // Container. Multiple failure modes stacked: scrollFactor=0 doesn't
    // propagate to children via `Container.add`, so the child's hit area
    // was computed against the WORLD coords it had pre-add (near 0,0),
    // not its post-add screen position. Result: clicks anywhere on the
    // panel hit nothing, hand cursor never appeared, refresh's interactive
    // toggle re-broke things on every gem update.
    //
    // The robust pattern is: make the CONTAINER itself interactive over
    // the full panel rect, then in the pointerdown handler check whether
    // the local-space pointer landed inside the button sub-rect. Phaser
    // hands us `localX/localY` already transformed through the container's
    // matrix (including its scrollFactor), so the check is straight
    // rect-contains math.
    //
    // Trade-off: hand cursor now appears anywhere on the panel, not only
    // on the button. Acceptable for a small HUD widget; the visual "Max"
    // / disabled-grey state still signals when the click is a no-op.
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.input!.cursor = 'pointer';
    this.container.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        localX: number,
        localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // Stop propagation so the scene-level fire handler doesn't ALSO
        // see this click (Bug 1 original symptom). Bug 1 Pass 2's
        // `containsPointer` guard in ArenaController already handles
        // this defensively, but `stopPropagation` is cleaner.
        event.stopPropagation();
        const insideButton =
          localX >= BUTTON_LOCAL_X &&
          localX <= BUTTON_LOCAL_X + BUTTON_WIDTH &&
          localY >= BUTTON_LOCAL_Y &&
          localY <= BUTTON_LOCAL_Y + BUTTON_HEIGHT;
        if (!insideButton) return;
        if (!this.buttonAffordable) return;
        this.handleClick();
      },
      this,
    );
  }

  private refresh(): void {
    if (this.destroyed || this.tierText === null) return;
    // PSV-1726 — next cost comes from the class tree. Both choices at a
    // given tier share one cost, so the first row is representative.
    const atMax = this.currentTier >= MAX_CLASS_TIER;
    // PSV-1906 — at the apex the button becomes ASCEND (enabled once the
    // stat board is maxed; the controller routes the click to the RPC).
    const ascendReady = atMax && this.statsMaxed;
    const nextRow = atMax ? null : this.cheapestNext();
    const cost = nextRow?.cost ?? 0;
    const affordable =
      ascendReady ||
      (!atMax && nextRow !== null && this.currentGems >= cost && this.statsMaxed);
    this.buttonAffordable = affordable;

    this.tierText.setText(`Tier: ${this.currentTier} / ${MAX_CLASS_TIER}`);
    if (atMax) {
      this.nextText?.setText(
        ascendReady
          ? 'ASCEND: reset for a permanent \u2605 +5% mining'
          : 'Apex reached — max all stats to ASCEND',
      );
    } else if (!this.statsMaxed) {
      // PSV-1727 — the real prerequisite comes first: max the stat board.
      const maxTotal = STAT_ORDER.length * MAX_STAT_LEVEL;
      this.nextText?.setText(
        `Stats ${this.statProgress}/${maxTotal} → then ${cost} gems`,
      );
    } else {
      this.nextText?.setText(`Next: ${cost} gems`);
    }

    if (this.buttonBg !== null) {
      this.buttonBg.clear();
      const bgColor = affordable ? COLOR_ENABLED : COLOR_DISABLED;
      this.buttonBg.fillStyle(bgColor, 0.85);
      this.buttonBg.fillRect(
        BUTTON_LOCAL_X,
        BUTTON_LOCAL_Y,
        BUTTON_WIDTH,
        BUTTON_HEIGHT,
      );
    }
    if (this.buttonLabel !== null) {
      this.buttonLabel.setText(
        atMax
          ? ascendReady
            ? 'ASCEND \u2605'
            : 'Max stats first'
          : this.statsMaxed
            ? 'Upgrade'
            : 'Max stats first',
      );
      this.buttonLabel.setColor(affordable ? '#ffffff' : '#888888');
    }
    // No `setInteractive` toggle on the container — it stays interactive
    // for the lifetime of the panel. The `buttonAffordable` flag inside
    // the pointerdown handler does the gating instead.
  }

  private handleClick(): void {
    if (this.destroyed) return;
    // PSV-1726 — gate on the class tree (mirrors refresh()'s affordability).
    // PSV-1727 — plus the all-stats-maxed prerequisite.
    if (!this.statsMaxed) return;
    // PSV-1906 — at the apex the click IS the ascend request (the
    // controller routes it to the Ascend RPC); the pre-ascension gate
    // swallowed it here and the ASCEND button read as dead.
    if (this.currentTier >= MAX_CLASS_TIER) {
      this.onClick();
      return;
    }
    const nextRow = this.cheapestNext();
    if (nextRow === null) return;
    if (this.currentGems < nextRow.cost) return;
    this.onClick();
  }
}
